import { Template } from 'crono-fe-common/types/template';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import useCreateTemplate from 'hooks/services/templates/useCreateTemplate';
import useGetTemplateAttachmentsMutate from 'hooks/services/templates/useGetTemplateAttachmentsMutate';

const SINGLE_USE_TITLE_FROM_CONTENT_LENGTH = 10;

/** Names a single use template after the one it was derived from. */
export const buildSingleUseTemplateTitle = (originalTitle: string) =>
  `${originalTitle} Copy`;

/**
 * A step written from scratch has no template to take a name from, so it is
 * named after its own opening words. Nobody is asked to invent one: the name
 * only exists so the step has something to show.
 */
export const buildSingleUseTemplateTitleFromContent = (content: string) => {
  const text = content
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= SINGLE_USE_TITLE_FROM_CONTENT_LENGTH) return text;

  return `${text.slice(0, SINGLE_USE_TITLE_FROM_CONTENT_LENGTH).trimEnd()}...`;
};

export const isTemplateAlreadyInStep = (
  templateId: number,
  step: StrategyStep,
  conditional: boolean,
) => {
  const target = conditional ? step.strategyStepConditional : step;
  if (!target) return false;

  return (
    target.templateId === templateId ||
    !!target.strategyStepTemplates?.some(
      (strategyStepTemplate) => strategyStepTemplate.templateId === templateId,
    )
  );
};

export const useSingleUseTemplateCopy = () => {
  const { mutateAsync: createTemplate } = useCreateTemplate();
  const { mutateAsync: getTemplateAttachments } =
    useGetTemplateAttachmentsMutate();

  const createSingleUseCopy = async (template: Template) => {
    const attachments =
      (await getTemplateAttachments(template.id))?.data?.data ?? [];

    const { data: response } = await createTemplate({
      ...template,
      title: buildSingleUseTemplateTitle(template.title),
      isSingleUse: true,
      attachmentIds: attachments.map((attachment) => attachment.id),
    });

    return response?.data?.id ?? null;
  };

  return { createSingleUseCopy };
};
